import React, { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import isValid from 'date-fns/isValid';

import { useSnackbar } from '@bucketplace/design-system';
import { bucketLog, BucketLogData } from '@bucketplace/bucket-log';
import { Viewer, DocumentNode, defaultComponents, LinkContextValue } from '@bucketplace/jsx-editor/exhibition';
import { CUSTOM_COUPON_PUBLISH_MESSAGE, getPublishCouponsMessage, Product, useKakaoAPI } from '@bucketplace/common';

import { OHS_HOST } from 'common';
import { useUserContext, useGlobalModal } from 'provider';
import { getRaffleEvent, getStoreItems, postRaffleEntry, publishProductCouponMolds } from 'promotions/api/promotion';
import { RaffleEntryResponse } from 'promotions/types';
import LogLink from 'components/Link/LogLink';
import { SuperLink } from 'components/Layout';
import { getAppOsInfo, checkAppVersion, sendBrazeLog } from 'utils';
import { createShareLink } from 'api/share-link';

// import { usePlatformInfo } from '../hooks/usePlatformInfo';

const DISABLE_COMPONENT_ID_LIST = ['snsShare', 'floatingLink', 'modal', 'comment'];
const DISPLAY_DATE_KEY = '82efaa2733534320a1d200cd69202842';

export const INITIAL_EDITOR_DATA: DocumentNode = {
  key: null,
  type: 'page',
  props: {},
  children: [],
};

const getEditorLink = (
  experimentType: string | null,
  callback: (targetId:string, smooth?: boolean) => void
): LinkContextValue => {
  return {
    element: ({ to, children, logData, ...props }) => {
      const isAnchor = typeof to === 'string' && to.includes('#');
      const href = typeof to === 'string' ? (isAnchor ? 'javascript:;' : to) : '';

      return (
        <LogLink
          linkType="a"
          href={href}
          logData={{
            ...logData,
            data: experimentType
              ? {
                experimentType,
              }
              : null,
          }}
          {...props}
        >
          { children }
        </LogLink>
      );
    },
    callbacks: {
      onClick: (targetId: string) => {
        callback(targetId, true);
      },
    },
  };
};

const hasStickyTabType = (node: DocumentNode): boolean => {
  if (node.type === 'stickyTab') {
    return true;
  }
  for (const child of node.children) {
    if (hasStickyTabType(child)) {
      return true;
    }
  }
  return false;
};

export interface SectionViewerProps {
  exhibitionId: string;
  units: string;
  kakaoTemplate: {
    templateId: number,
    templateArgs: any,
  };
  experimentType?: string | null;
  onViewerLoaded: (isLoaded: boolean) => void;
  onClick: (targetId: string, smooth?: boolean) => void;
}

function _SectionViewer ({
  exhibitionId,
  units,
  kakaoTemplate,
  experimentType,
  onViewerLoaded,
  onClick,
}: SectionViewerProps): ReactElement {
  const { query, asPath } = useRouter();
  const { openConfirmModal, openAlertModal } = useGlobalModal();
  const { open } = useSnackbar();
  const { userInfo, pending } = useUserContext();
  const isLoggedIn = !!userInfo?.user;
  const { osType, osVersion } = getAppOsInfo() ?? {};
  const kakaoApi = useKakaoAPI();

  let showCouponBadge = true;

  if (!checkAppVersion({
    osType,
    currentVersion: osVersion,
    androidVersion: '23.5.0',
    iosVersion: '23.5.0',
  })) {
    showCouponBadge = false;
  }
  const [isViewerLoaded, setIsViewerLoaded] = useState(false);

  const displayDateValue = (query[DISPLAY_DATE_KEY] ?? '') as string;
  const displayDate = isValid(new Date(displayDateValue)) ? new Date(displayDateValue) : undefined;

  const linkContextValue = getEditorLink(experimentType, onClick);

  const onRedirectToSignIn = async (): Promise<void> => {
    const path = `/events/${asPath}`;
    const signInUrl = `${OHS_HOST}/users/sign_in?redirect_to=${encodeURIComponent(path)}`;

    if (await openConfirmModal({
      message: '로그인 후 다운로드가 가능합니다.',
    })) {
      window.location.href = signInUrl;
    }
  };

  const handleCouponClick = async (cartCouponMoldIds: string[], productCouponMoldIds: string[]): Promise<void> => {
    if (pending) {
      await openAlertModal({ message: '접속 인원이 많아 페이지 로딩이 지연되고 있습니다. 다시 시도해주세요.' });
      return;
    }

    if (!isLoggedIn) {
      await onRedirectToSignIn();
      return;
    }

    try {
      const couponLists = [...cartCouponMoldIds, ...productCouponMoldIds];
      const coupons = await Promise.all(couponLists.filter(Boolean).map(
        async (id) => await publishProductCouponMolds({
          id: Number(id),
          userId: userInfo.user.userId.toString(),
        })
      )).then(result => result.flatMap(coupon => coupon));

      const message = getPublishCouponsMessage(coupons);

      await openAlertModal({ message });
    } catch (error) {
      console.error(JSON.stringify(error));
      await openAlertModal({ message: CUSTOM_COUPON_PUBLISH_MESSAGE.ERROR });
      return;
    }
  };

  const handleGetStoreItems = async (ids: string[]): Promise<Product[]> => {
    const items = await getStoreItems({
      ids,
      isLoggedIn,
      showCouponBadge: showCouponBadge ?? false,
    });
    return items;
  };

  const handlePostRaffleEntry = async (eventId: string): Promise<RaffleEntryResponse | null> => {
    if (!isLoggedIn) {
      await onRedirectToSignIn();
      return null;
    }

    const res = await postRaffleEntry(eventId, exhibitionId);
    return res;
  };

  const handleGetStoreItemHref = (productId: number): string => `${OHS_HOST}/productions/${productId}/selling`;

  const handleSendBucketLog = (logData?: BucketLogData): void => {
    if (logData) {
      bucketLog({
        category: logData.category ?? 'click',
        ...logData,
      });
    }
  };

  const handleImageMapBrazeClick = (customEventName: string): void => {
    const logData = {
      customEventName,
      exhibitionId,
      date: new Date().toISOString(),
      userId: userInfo?.user?.userId,
      // Web에서 osType이 'undefined' 로 넘어옴
      osType: osType != null && osType !== 'undefined' ? osType : 'Web',
      osVersion: osVersion,
    };

    sendBrazeLog(customEventName, logData);

    // if (isInApp) {
    //   sendBrazeAppLog(customEventName, logData);
    // } else {
    //   sendBrazeLog(customEventName, logData);
    // }
  };

  const handleImageMapBrazeSignin = async (): Promise<void> => {
    const path = `/events/${asPath}`;
    const signInUrl = `${OHS_HOST}/users/sign_in?redirect_to=${encodeURIComponent(path)}`;

    if (await openConfirmModal({
      message: '로그인 후 이용이 가능합니다.',
    })) {
      window.location.href = signInUrl;
    }
  };

  const handleImageMapShareClick = async (): Promise<void> => {
    const request = {
      locationHref: typeof window === 'undefined' ? '' : window.location.href,
      onlyWeb: false,
      contentId: Number(exhibitionId),
      contentType: 'Exhibition',
      userId: userInfo?.user?.userId != null ? Number(userInfo?.user?.userId) : undefined,
    };
    /** @ https://wolfgangrittner.dev/how-to-use-clipboard-api-in-firefox/ */
    if (typeof ClipboardItem && navigator.clipboard.write) {
      // NOTE: Safari locks down the clipboard API to only work when triggered
      //   by a direct user interaction. You can't use it async in a promise.
      //   But! You can wrap the promise in a ClipboardItem, and give that to
      //   the clipboard API.
      //   Found this on https://developer.apple.com/forums/thread/691873
      const text = new ClipboardItem({
        'text/plain': createShareLink(request)
          .then(response => response.url ?? window.location.href)
          .then(text => new Blob([text], { type: 'text/plain' })),
      });
      navigator.clipboard.write([text])
        .then(() => {
          open({ content: '클립보드에 복사했습니다.' });
        })
        .catch(err => {
          console.error('Could not copy text: ', err);
          open({ content: '클립보드 복사에 실패했습니다.' });
        });}
    else {
      // NOTE: Firefox has support for ClipboardItem and navigator.clipboard.write,
      //   but those are behind `dom.events.asyncClipboard.clipboardItem` preference.
      //   Good news is that other than Safari, Firefox does not care about
      //   Clipboard API being used async in a Promise.
      createShareLink(request)
        .then(response => response.url ?? window.location.href)
        .then(text => navigator.clipboard.writeText(text)
          .then(() => {
            open({ content: '클립보드에 복사했습니다.' });
          })
          .catch(err => {
            console.error('Could not copy text: ', err);
            open({ content: '클립보드 복사에 실패했습니다.' });
          })
        );
    }
  };

  const document = useMemo(() => {
    return units ? JSON.parse(units) : INITIAL_EDITOR_DATA;
  }, [units]);

  useEffect(() => {
    onViewerLoaded(isViewerLoaded);
  }, [isViewerLoaded, onViewerLoaded]);

  if (typeof window === 'undefined' || !units || pending) {
    return null;
  }

  return (
    // eslint-disable-next-line react/no-unknown-property
    <div onLoad={() => setIsViewerLoaded(true)}>
      <Viewer
        document={document}
        components={defaultComponents}
        displayDate={displayDate}
        disableComponentIds={DISABLE_COMPONENT_ID_LIST}
        editorInfoContext={{
          onSendLog: handleSendBucketLog,
          uploadEndpoint: '',
          isInitialLoadAllContent: true,
        }}
        unitContext={{
          storeItems: {
            getStoreItems: handleGetStoreItems,
            getStoreItemHref: handleGetStoreItemHref,
            linkComponent: SuperLink,
          },
          coupon: {
            onClick: handleCouponClick,
          },
          imageMapShare: {
            kakaoApi,
            kakaoTemplateId: kakaoTemplate.templateId,
            templateArgs: kakaoTemplate.templateArgs,
            onClick: handleImageMapShareClick,
          },
          imageMapBraze: {
            isLoggedIn,
            onClick: handleImageMapBrazeClick,
            onSignin: handleImageMapBrazeSignin,
          },
          link: linkContextValue,
          raffle: {
            getRaffleEvent,
            postRaffleEntry: handlePostRaffleEntry,
          },
        }}
      />
    </div>
  );
}

export const SectionViewer = memo(_SectionViewer);
